// CTSK-525 — Documents list view mode (masonry / grid / list), persisted per
// user + org in localStorage. Own key: must not collide with
// tasko.documents.cols.v1 / .detail.drawer.v1 / .hidden-nests.v1.

export type ViewMode = 'masonry' | 'grid' | 'list';
export const VIEW_PREFS_KEY = 'tasko.documents.view-prefs.v1';
export const DEFAULT_VIEW_MODE: ViewMode = 'list';

const MODES: readonly ViewMode[] = ['masonry', 'grid', 'list'];

function scope(userId: string | null | undefined, orgId: string | null | undefined): string {
  return `${userId ?? 'anon'}:${orgId ?? 'none'}`;
}

function readAll(): Record<string, unknown> {
  try {
    const raw = window.localStorage.getItem(VIEW_PREFS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function loadViewMode(userId?: string | null, orgId?: string | null): ViewMode {
  const v = readAll()[scope(userId, orgId)];
  return MODES.includes(v as ViewMode) ? (v as ViewMode) : DEFAULT_VIEW_MODE;
}

export function saveViewMode(mode: ViewMode, userId?: string | null, orgId?: string | null): void {
  try {
    const all = readAll();
    all[scope(userId, orgId)] = mode;
    window.localStorage.setItem(VIEW_PREFS_KEY, JSON.stringify(all));
  } catch {
    /* storage unavailable — preference is session-only */
  }
}

/** Card modes need room; below 900px the list is forced (saved pref untouched). */
export function effectiveViewMode(saved: ViewMode, wide: boolean): ViewMode {
  return wide ? saved : 'list';
}

/** Masonry column count: 3 at >=1300px, 2 at >=900px, else 1. */
export function docColumnCount(containerWidth: number, override?: number | null): number {
  if (override && override >= 1) return override;
  if (containerWidth >= 1300) return 3;
  if (containerWidth >= 900) return 2;
  return 1;
}

// Extra per-scope settings stored beside the mode: `<scope>:cols` (2|3|4, absent = auto)
// and `<scope>:previews` (true = every card shows its preview).
export type ColsPref = 2 | 3 | 4 | null;

export function loadCols(userId?: string | null, orgId?: string | null): ColsPref {
  const v = readAll()[`${scope(userId, orgId)}:cols`];
  return v === 2 || v === 3 || v === 4 ? v : null;
}
export function loadPreviewAll(userId?: string | null, orgId?: string | null): boolean {
  return readAll()[`${scope(userId, orgId)}:previews`] === true;
}
function saveExtra(key: string, value: unknown, userId?: string | null, orgId?: string | null): void {
  try {
    const all = readAll();
    const k = `${scope(userId, orgId)}:${key}`;
    if (value === null || value === false) delete all[k]; else all[k] = value;
    window.localStorage.setItem(VIEW_PREFS_KEY, JSON.stringify(all));
  } catch { /* session-only */ }
}
export function saveCols(v: ColsPref, userId?: string | null, orgId?: string | null): void { saveExtra('cols', v, userId, orgId); }
export function savePreviewAll(v: boolean, userId?: string | null, orgId?: string | null): void { saveExtra('previews', v, userId, orgId); }
export function loadScaled(userId?: string | null, orgId?: string | null): boolean {
  return readAll()[`${scope(userId, orgId)}:scaled`] === true;
}
export function saveScaled(v: boolean, userId?: string | null, orgId?: string | null): void { saveExtra('scaled', v, userId, orgId); }

// ── Return-highlight effect (Settings > Documents) + last-opened breadcrumb ──
export type HighlightEffect = 'off' | 'shimmer' | 'smoke' | 'pulse';
export const HIGHLIGHT_EFFECTS: readonly HighlightEffect[] = ['off', 'shimmer', 'smoke', 'pulse'];
export const CARD_EFFECT_KEY = 'tasko.documents.card-effect.v1';
export const LAST_OPENED_KEY = 'tasko.documents.last-opened.v1';

export function loadHighlightEffect(): HighlightEffect {
  try {
    const v = window.localStorage.getItem(CARD_EFFECT_KEY);
    return HIGHLIGHT_EFFECTS.includes(v as HighlightEffect) ? (v as HighlightEffect) : 'shimmer';
  } catch { return 'shimmer'; }
}
export function saveHighlightEffect(v: HighlightEffect): void {
  try { window.localStorage.setItem(CARD_EFFECT_KEY, v); } catch { /* session-only */ }
}
export function rememberOpened(id: string): void {
  try { window.sessionStorage.setItem(LAST_OPENED_KEY, id); } catch { /* ignore */ }
}
/** Read-and-clear: the doc the user just came back from, if any. */
export function takeLastOpened(): string | null {
  try {
    const v = window.sessionStorage.getItem(LAST_OPENED_KEY);
    if (v) window.sessionStorage.removeItem(LAST_OPENED_KEY);
    return v;
  } catch { return null; }
}

// ── Hover enlargement (Settings > Documents) ──
export type HoverZoom = 'current' | 'm' | 'l';
export const HOVER_ZOOMS: readonly HoverZoom[] = ['current', 'm', 'l'];
export const HOVER_ZOOM_SCALE: Record<HoverZoom, number> = { current: 1, m: 1.06, l: 1.14 };
export const HOVER_ZOOM_KEY = 'tasko.documents.hover-zoom.v1';
export function loadHoverZoom(): HoverZoom {
  try {
    const v = window.localStorage.getItem(HOVER_ZOOM_KEY);
    return HOVER_ZOOMS.includes(v as HoverZoom) ? (v as HoverZoom) : 'm';
  } catch { return 'm'; }
}
export function saveHoverZoom(v: HoverZoom): void {
  try { window.localStorage.setItem(HOVER_ZOOM_KEY, v); } catch { /* session-only */ }
}
